<template>
    <v-container class="px-5">
        <v-row>
            <v-col>
                <h4>Verification &amp; OTP Audit Log</h4>
                <p class="text-caption text-grey">
                    Append-only record of every email/SMS code sent and every validation attempt, with request context. Rows are never edited or deleted.
                </p>
            </v-col>
        </v-row>

        <v-row dense>
            <v-col cols="12" md="4">
                <v-text-field v-model="search" density="compact" label="Search recipient, IP, or user"
                              prepend-inner-icon="mdi-magnify" clearable variant="outlined" hide-details
                              @update:model-value="debouncedFetch"/>
            </v-col>
            <v-col cols="6" md="2">
                <v-select v-model="channel" :items="channelOptions" label="Channel" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
            <v-col cols="6" md="3">
                <v-select v-model="purpose" :items="purposeOptions" label="Purpose" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
            <v-col cols="6" md="2">
                <v-select v-model="event" :items="eventOptions" label="Event" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
            <v-col cols="6" md="1">
                <v-select v-model="success" :items="successOptions" label="Result" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table-server v-model:items-per-page="perPage" v-model:page="page"
                                     :headers="headers" :items="logs" :items-length="totalItems"
                                     :loading="loading" :items-per-page-options="[25, 50, 100]"
                                     @update:options="onOptions">
                    <template v-slot:item.created_at="{ item }">
                        <span class="text-no-wrap">{{ formatDate(item.created_at) }}</span>
                    </template>
                    <template v-slot:item.channel="{ item }">
                        <v-chip size="x-small" variant="tonal" :color="item.channel === 'sms' ? 'green' : 'blue'">
                            <v-icon start size="12">{{ item.channel === 'sms' ? 'mdi-message-text' : 'mdi-email' }}</v-icon>
                            {{ item.channel }}
                        </v-chip>
                    </template>
                    <template v-slot:item.purpose="{ item }">
                        <v-chip size="x-small" variant="tonal">{{ item.purpose }}</v-chip>
                    </template>
                    <template v-slot:item.event="{ item }">
                        <v-chip size="x-small" variant="tonal" :color="eventColor(item.event)">{{ item.event }}</v-chip>
                    </template>
                    <template v-slot:item.user="{ item }">
                        <div v-if="item.user">
                            {{ item.user.first_name }} {{ item.user.last_name }}
                            <div class="text-caption text-grey">{{ item.user.email }}</div>
                        </div>
                        <span v-else class="text-caption text-grey">-</span>
                    </template>
                    <template v-slot:item.code="{ item }">
                        <code v-if="item.code">{{ item.code }}</code>
                        <span v-else class="text-grey">-</span>
                    </template>
                    <template v-slot:item.success="{ item }">
                        <v-icon v-if="item.success === true" color="success" size="18">mdi-check-circle</v-icon>
                        <v-icon v-else-if="item.success === false" color="error" size="18">mdi-close-circle</v-icon>
                        <span v-else class="text-grey">-</span>
                    </template>
                    <template v-slot:item.client_source="{ item }">
                        <v-chip v-if="item.client_source && item.client_source !== 'unknown'" size="x-small" variant="tonal"
                                :color="item.client_source === 'web' ? 'blue' : item.client_source === 'app' ? 'green' : 'grey'">
                            <v-icon start size="12">{{ item.client_source === 'app' ? 'mdi-cellphone' : item.client_source === 'web' ? 'mdi-web' : 'mdi-help-circle-outline' }}</v-icon>
                            {{ item.client_source }}
                        </v-chip>
                        <span v-else class="text-grey">-</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn icon size="x-small" variant="text" @click="showContext(item)">
                            <v-icon size="18">mdi-information-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Context</v-tooltip>
                        </v-btn>
                    </template>
                </v-data-table-server>
            </v-col>
        </v-row>

        <!-- Context detail dialog -->
        <v-dialog v-model="contextDialog" max-width="650">
            <v-card v-if="contextTarget">
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>Verification event</span>
                    <v-btn icon size="small" variant="text" @click="contextDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-table density="compact" class="border rounded">
                        <tbody>
                            <tr><td class="font-weight-medium" width="150">Time</td><td>{{ formatDate(contextTarget.created_at) }}</td></tr>
                            <tr><td class="font-weight-medium">Channel</td><td>{{ contextTarget.channel }}</td></tr>
                            <tr><td class="font-weight-medium">Purpose</td><td>{{ contextTarget.purpose }}</td></tr>
                            <tr><td class="font-weight-medium">Event</td><td>{{ contextTarget.event }}</td></tr>
                            <tr><td class="font-weight-medium">Recipient</td><td>{{ contextTarget.recipient || '-' }}</td></tr>
                            <tr><td class="font-weight-medium">Code</td><td><code>{{ contextTarget.code || '-' }}</code></td></tr>
                            <tr v-if="contextTarget.success !== null"><td class="font-weight-medium">Result</td><td>{{ contextTarget.success ? 'Passed' : 'Failed' }}</td></tr>
                            <tr v-if="contextTarget.detail"><td class="font-weight-medium">Detail</td><td>{{ contextTarget.detail }}</td></tr>
                            <tr v-if="contextTarget.user"><td class="font-weight-medium">User</td><td>{{ contextTarget.user.first_name }} {{ contextTarget.user.last_name }} ({{ contextTarget.user.email }})</td></tr>
                            <tr><td class="font-weight-medium">IP Address</td><td>{{ contextTarget.ip_address || '-' }}</td></tr>
                            <tr v-if="contextTarget.client_source"><td class="font-weight-medium">Client</td><td>{{ contextTarget.client_source }}</td></tr>
                            <tr v-if="contextTarget.app_version"><td class="font-weight-medium">App Version</td><td>{{ contextTarget.app_version }}</td></tr>
                            <tr v-if="contextTarget.browser_name"><td class="font-weight-medium">Browser</td><td>{{ contextTarget.browser_name }} {{ contextTarget.browser_version }}</td></tr>
                            <tr v-if="contextTarget.os_name"><td class="font-weight-medium">OS</td><td>{{ contextTarget.os_name }} {{ contextTarget.os_version }}</td></tr>
                            <tr v-if="contextTarget.device_model"><td class="font-weight-medium">Device</td><td>{{ contextTarget.device_model }}</td></tr>
                            <tr v-if="contextTarget.accept_language"><td class="font-weight-medium">Language</td><td>{{ contextTarget.accept_language }}</td></tr>
                            <tr v-if="contextTarget.referer"><td class="font-weight-medium">Referer</td><td class="text-caption" style="word-break: break-all;">{{ contextTarget.referer }}</td></tr>
                            <tr v-if="contextTarget.user_agent"><td class="font-weight-medium">User Agent</td><td class="text-caption" style="word-break: break-all;">{{ contextTarget.user_agent }}</td></tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {ref} from 'vue'
import {send} from '@/composables/apiClient'
import {useSnackbarStore} from '@/stores/snackBarStore'

const snackbar = useSnackbarStore()
const loading = ref(false)
const logs = ref([])
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(25)
const search = ref('')
const channel = ref(null)
const purpose = ref(null)
const event = ref(null)
const success = ref(null)
const sortBy = ref(null)

const contextDialog = ref(false)
const contextTarget = ref(null)

let debounceTimer = null

const channelOptions = [
    {title: 'Email', value: 'email'},
    {title: 'SMS', value: 'sms'},
]
const purposeOptions = [
    {title: 'Signup', value: 'signup'},
    {title: 'Password reset', value: 'password_reset'},
    {title: 'Registration (phone)', value: 'registration'},
    {title: 'Driver login', value: 'driver_login'},
]
const eventOptions = [
    {title: 'Sent', value: 'sent'},
    {title: 'Validated', value: 'validated'},
    {title: 'Failed', value: 'failed'},
    {title: 'Rate limited', value: 'rate_limited'},
    {title: 'Blocked', value: 'blocked'},
]
const successOptions = [
    {title: 'Passed', value: 'true'},
    {title: 'Failed', value: 'false'},
]

const headers = [
    {title: 'Time', key: 'created_at', sortable: true},
    {title: 'Channel', key: 'channel', sortable: true},
    {title: 'Purpose', key: 'purpose', sortable: true},
    {title: 'Event', key: 'event', sortable: true},
    {title: 'Recipient', key: 'recipient', sortable: true},
    {title: 'User', key: 'user', sortable: false},
    {title: 'Code', key: 'code', sortable: false},
    {title: 'Result', key: 'success', sortable: true, align: 'center'},
    {title: 'Source', key: 'client_source', sortable: true, align: 'center'},
    {title: 'IP', key: 'ip_address', sortable: true},
    {title: '', key: 'actions', sortable: false, align: 'end'},
]

// Header key -> DB column the server may order by. The API validates against
// its own whitelist too; this just keeps the two in step.
const SORT_KEYS = {
    created_at: 'created_at',
    channel: 'channel',
    purpose: 'purpose',
    event: 'event',
    recipient: 'recipient',
    success: 'success',
    client_source: 'client_source',
    ip_address: 'ip_address',
}

const eventColor = (e) => ({sent: 'blue', validated: 'success', failed: 'error', rate_limited: 'warning', blocked: 'orange'}[e] || 'grey')

const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
}

const debouncedFetch = () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
        page.value = 1
        fetchLogs()
    }, 300)
}

const resetFetch = () => {
    page.value = 1
    fetchLogs()
}

const fetchLogs = () => {
    loading.value = true
    const sort = sortBy.value
    send('get', 'admin/verification-logs', {
        page: page.value,
        per_page: perPage.value,
        search: search.value || undefined,
        channel: channel.value || undefined,
        purpose: purpose.value || undefined,
        event: event.value || undefined,
        success: success.value ?? undefined,
        sort: sort ? SORT_KEYS[sort.key] : undefined,
        direction: sort ? sort.order : undefined,
    }).then((response) => {
        const data = response.data.data
        logs.value = data.data
        totalItems.value = data.total
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to load verification logs', 'error')
    }).finally(() => loading.value = false)
}

// The data table fires this on mount and whenever page/sort change. Capture the
// active sort; a sort change jumps back to page 1 (which re-fires this event and
// fetches), otherwise fetch here.
const onOptions = (options) => {
    const next = options.sortBy?.[0] || null
    const sortChanged = JSON.stringify(next) !== JSON.stringify(sortBy.value)
    sortBy.value = next
    if (sortChanged && page.value !== 1) {
        page.value = 1
        return
    }
    fetchLogs()
}

const showContext = (item) => {
    contextTarget.value = item
    contextDialog.value = true
}
</script>
